import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AttendanceStatus, Dancer } from '@pirouette/shared';
import { useSessionAttendance, useMarkAttendance } from '@/data/sessions';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/faculty/classes/$id/attendance')({
  component: AttendanceMark,
});

interface Session {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  cancelled: boolean;
  notes: string | null;
}

function useUpcomingSessions(classId: string) {
  return useQuery({
    queryKey: ['faculty-sessions', classId],
    queryFn: async (): Promise<Session[]> => {
      const today = new Date().toISOString().slice(0, 10);
      // Window of 14 days back + 60 forward so faculty can mark recent
      // missed classes and look at the next two months at a glance.
      const past = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const future = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('class_sessions')
        .select('id, session_date, start_time, end_time, cancelled, notes')
        .eq('class_id', classId)
        .gte('session_date', past)
        .lte('session_date', future)
        .order('session_date', { ascending: false });
      if (error) throw error;
      // Prefer today's session if one exists. COPY the array first —
      // React Query returns a stable reference and splice() would mutate
      // the shared cache, silently corrupting later reads on the same key.
      const rows = [...((data ?? []) as Session[])];
      const todayIdx = rows.findIndex((r) => r.session_date === today);
      if (todayIdx > 0) {
        const [t] = rows.splice(todayIdx, 1);
        if (t) rows.unshift(t);
      }
      return rows;
    },
  });
}

function useRoster(classId: string) {
  return useQuery({
    queryKey: ['class-roster', classId],
    queryFn: async (): Promise<Dancer[]> => {
      const { data, error } = await supabase
        .from('enrollments')
        .select('dancers(*)')
        .eq('class_id', classId)
        .eq('status', 'enrolled')
        .is('deleted_at', null);
      if (error) throw error;
      interface Row {
        dancers: Dancer | null;
      }
      return ((data ?? []) as Row[])
        .map((r) => r.dancers)
        .filter((d): d is Dancer => d !== null && d.deleted_at === null)
        .sort((a, b) => a.last_name.localeCompare(b.last_name));
    },
  });
}

const STATUS_OPTIONS: {
  value: AttendanceStatus;
  label: string;
  tone: 'success' | 'warn' | 'danger' | 'info';
}[] = [
  { value: 'present', label: 'Present', tone: 'success' },
  { value: 'late', label: 'Late', tone: 'warn' },
  { value: 'excused', label: 'Excused', tone: 'info' },
  { value: 'absent', label: 'Absent', tone: 'danger' },
];

function AttendanceMark() {
  const { id: classId } = Route.useParams();
  const sessions = useUpcomingSessions(classId);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const effectiveSessionId = sessionId ?? sessions.data?.[0]?.id;

  const roster = useRoster(classId);
  const attendance = useSessionAttendance(effectiveSessionId);
  const markAttendance = useMarkAttendance();

  const attendanceByDancer = new Map<string, AttendanceStatus>();
  for (const a of attendance.data ?? []) {
    attendanceByDancer.set(a.dancer_id, a.status);
  }

  async function setStatus(dancerId: string, status: AttendanceStatus) {
    if (!effectiveSessionId) return;
    try {
      await markAttendance.mutateAsync({
        class_session_id: effectiveSessionId,
        dancer_id: dancerId,
        status,
      });
    } catch (err) {
      toast({
        title: "Couldn't save attendance",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/faculty/classes"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← My classes
        </Link>
        <h1 className="mt-2">Attendance</h1>
      </header>

      <section aria-labelledby="session-pick" className="surface-elevated p-5">
        <label htmlFor="session-select" className="block font-medium text-[var(--text-sm)]">
          Session
        </label>
        <select
          id="session-select"
          className="mt-2 h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
          value={effectiveSessionId ?? ''}
          onChange={(e) => setSessionId(e.target.value)}
        >
          {sessions.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.session_date} · {s.start_time}–{s.end_time}
              {s.cancelled ? ' (cancelled)' : ''}
            </option>
          ))}
        </select>
      </section>

      {roster.isLoading || attendance.isLoading ? (
        <p aria-live="polite">Loading…</p>
      ) : roster.data?.length === 0 ? (
        <p className="text-[var(--color-ink-muted)]">No dancers enrolled in this class.</p>
      ) : (
        <ul className="grid gap-2">
          {roster.data?.map((d) => {
            const current = attendanceByDancer.get(d.id);
            return (
              <li
                key={d.id}
                className="surface flex flex-wrap items-center justify-between gap-3 p-3"
              >
                <div>
                  <p className="font-medium">
                    {d.preferred_name ?? d.first_name} {d.last_name}
                  </p>
                  {current && (
                    <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
                      Current:{' '}
                      <Badge
                        tone={STATUS_OPTIONS.find((o) => o.value === current)?.tone ?? 'neutral'}
                      >
                        {current}
                      </Badge>
                    </p>
                  )}
                </div>
                <div
                  role="group"
                  aria-label={`Set attendance for ${d.first_name} ${d.last_name}`}
                  className="inline-flex rounded-[var(--radius-md)] border border-[var(--color-border)]"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => void setStatus(d.id, opt.value)}
                      aria-pressed={current === opt.value}
                      className="px-3 py-1.5 text-[var(--text-sm)] first:rounded-l-[var(--radius-md)] last:rounded-r-[var(--radius-md)] hover:bg-[var(--color-surface-sunken)] aria-[pressed=true]:bg-[var(--color-accent-muted)] aria-[pressed=true]:font-medium"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
